import { useState } from "react";
import { ApiError, type ChatMessage, type LedgerEvent, type SessionSummary } from "../lib/api";
import { MarkdownText } from "../components/MarkdownText";
import { Icon } from "../components/Icon";
import { askedByAnyone } from "../studio/project";
import { chatHref } from "../connections/navigation";
import { agentHref, sendAgentMessage } from "./api";

export interface RecentChoice { sessionId: string; message: ChatMessage }

/** What a person typed at this console. */
export function ownerInputs(inputs: LedgerEvent[]) {
  return inputs.filter(event => event.kind === "l0.inbound"
    && event.row.channel === "workbench" && event.row.origin === "human" && event.row.cause === "channel" && askedByAnyone(event.row));
}

/** The existing session list is newest first. Keep the console's own conversations before taking its recent window. */
export function ownerConversations(sessions: SessionSummary[], inputs: LedgerEvent[]) {
  const visible = new Set(ownerInputs(inputs).map(event => event.session_id));
  return sessions.filter(session => visible.has(session.id)).slice(0, 3);
}

/** A recent option reply without subsequent ordinary input, not an unanswered-question ledger. */
export function recentChoice(messages: ChatMessage[], sessionId: string): RecentChoice | null {
  let choice: ChatMessage | null = null;
  for (const message of messages) {
    if (message.session_id !== sessionId) continue;
    if (message.kind === "message" && message.role === "user") choice = null;
    else if (message.kind === "reply" && message.state === "sent" && !message.payload?.approval && message.options?.length) choice = message;
  }
  return choice ? { sessionId, message: choice } : null;
}

export function RecentChoiceCard({ choice, agentId, disabled, onSent }: {
  choice: RecentChoice; agentId: string; disabled: boolean; onSent: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const send = async (value: string) => {
    if (sending || submitted || disabled) return;
    setSending(true); setError("");
    try {
      await sendAgentMessage(agentId, choice.sessionId, value);
      setSubmitted(true);
      onSent();
    } catch (failure) {
      const problem = ApiError.fromUnknown(failure);
      if (problem.status >= 400 && problem.status < 500) setError(problem.detail || problem.title);
      else { setSubmitted(true); setError("发送状态未确认，请打开原对话核对。"); onSent(); }
    } finally { setSending(false); }
  };
  return <article className="recent-choice">
    <header><Icon name="chat" size={18} /><h3>最近对话中的选择</h3></header>
    <div className="recent-choice-text" data-quoted="true"><MarkdownText text={choice.message.text} breaks /></div>
    <div className="recent-choice-options">{choice.message.options.map(option => <button key={option.value} type="button" className="btn"
      disabled={disabled || sending || submitted} onClick={() => void send(option.value)}><span data-quoted="true">{option.label}</span><Icon name="arrow" size={15} /></button>)}</div>
    {sending || submitted ? <p role="status">{sending ? "正在发送选择…" : error ? "请先核对发送结果。" : "选择已提交，等待原对话读回；可以打开原对话核对。"}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    <a className="btn btn-quiet" href={agentHref(agentId, chatHref(choice.sessionId))}>打开原对话</a>
  </article>;
}
